// JOBE-0044.10.2: DocumentKindsProvider — route-level provider that
// owns the single GET /api/documents/kinds fetch and broadcasts the
// result via DocumentKindsContext. Mounted by LibraryListPage as a
// peer of EntityTypesProvider so the rail's TypeRailBlock (tree mode)
// and the composer's TypeValuePopover read a single source of truth.
//
// Tri-state transitions:
//   - mount → { status: 'loading', kinds: [], error: null }
//   - fetch ok → { status: 'ready', kinds: <rows>, error: null }
//   - transport / non-2xx → { status: 'error', kinds: [], error: '<msg>' }
//
// `retry` re-issues the fetch (resets to `loading`, then transitions
// per the success/error branches). The retry seam matches the
// pre-existing TypeRailBlock useDocumentKinds hook contract.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentKindsContext } from './DocumentKindsContext.js';

const INITIAL_STATE = { status: 'loading', kinds: [], error: null };

export function DocumentKindsProvider({ children }) {
  const [state, setState] = useState(INITIAL_STATE);
  // Bumped by retry() to re-run the fetch effect.
  const [tick, setTick] = useState(0);
  // Guard against double-fetch under React.StrictMode's intentional
  // double-effect-invoke in dev. The provider's contract is "one
  // fetch per route mount" — StrictMode double-invocation should not
  // surface as two network calls.
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (tick === 0 && fetchedRef.current) return undefined;
    fetchedRef.current = true;
    let alive = true;
    const ctrl = new AbortController();
    if (tick !== 0) setState(INITIAL_STATE);
    fetch('/api/documents/kinds', {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setState({
            status: 'error',
            kinds: [],
            error: `http_${r.status}`,
          });
          return;
        }
        const j = await r.json();
        if (!alive) return;
        const kinds = Array.isArray(j?.kinds) ? j.kinds : [];
        setState({ status: 'ready', kinds, error: null });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setState({
          status: 'error',
          kinds: [],
          error: err?.message || String(err),
        });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [tick]);

  const retry = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  // The `{ ...state, retry }` shape is intentionally a fresh object on
  // every state transition (loading → ready → error → ...). Consumers
  // that need reference-stable identity for an unrelated re-render
  // assert on `.kinds` (or another inner field) rather than on the
  // value object itself — see `DocumentKindsProvider.test.jsx`
  // ("stable across unrelated re-renders") for the canonical pattern.
  // EntityTypesProvider memoises `state` directly because it has no
  // `retry` seam; the asymmetry is by design.
  const value = useMemo(
    () => ({ ...state, retry }),
    [state, retry],
  );

  return (
    <DocumentKindsContext.Provider value={value}>
      {children}
    </DocumentKindsContext.Provider>
  );
}
